import { Button } from "@/components/ui/button";
import { app_env, env_config } from "@/env-config";
import { Building2, Clock, LifeBuoy, RefreshCw, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

export type OrganizationUnavailableReason =
  | "not-found"
  | "suspended"
  /** Deactivated automatically after a period with no sales activity. */
  | "inactive";

type Props = {
  reason: OrganizationUnavailableReason;
  /** Sub-domain the user tried to open, e.g. `acme.one-suite.cloud`. */
  domain?: string;
  /** Name of the workspace, only known when it exists but is unavailable. */
  organizationName?: string;
  /** Last sale / fired ticket, shown for the `inactive` reason. */
  lastActivityAt?: string | null;
};

/**
 * Full-screen, branded screen shown when a tenant workspace cannot be served:
 * the workspace does not exist, was suspended, or was deactivated for
 * inactivity. One screen for all three so the lock-out story stays in one
 * place — the copy and icon change, the shape does not.
 */
export default function OrganizationUnavailable({
  reason,
  domain,
  organizationName,
  lastActivityAt,
}: Props) {
  const { t } = useTranslation("organizationStatus");
  const brand = env_config[app_env];

  const supportEmail = `support@${brand?.domain ?? "one-suite.cloud"}`;
  const mainSite = `https://${brand?.domain ?? "one-suite.cloud"}`;

  const isUnavailable = reason === "suspended" || reason === "inactive";

  const icon =
    reason === "suspended" ? (
      <ShieldAlert className="size-8" />
    ) : reason === "inactive" ? (
      <Clock className="size-8" />
    ) : (
      <Building2 className="size-8" />
    );

  const title =
    reason === "suspended"
      ? t("suspended.title", { defaultValue: "This workspace is suspended" })
      : reason === "inactive"
        ? t("inactive.title", { defaultValue: "This workspace is inactive" })
        : t("notFound.title", { defaultValue: "Workspace not found" });

  const description =
    reason === "suspended"
      ? t("suspended.description", {
          name: organizationName,
          defaultValue:
            "Access to this workspace is temporarily disabled. Please contact your administrator or our support team to restore it.",
        })
      : reason === "inactive"
        ? t("inactive.description", {
            name: organizationName,
            date: lastActivityAt
              ? new Date(lastActivityAt).toLocaleDateString()
              : undefined,
            defaultValue:
              "This workspace was deactivated after a long period without any sales activity. Nothing has been deleted — contact our support team to switch it back on.",
          })
        : t("notFound.description", {
            defaultValue:
              "We couldn't find a workspace at this address. Check the link for typos, or ask your administrator for the correct one.",
          });

  return (
    <div className="w-screen h-dvh bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md flex flex-col items-center text-center gap-6">
        <img className="h-9" src={brand?.logo} alt={brand?.name} />

        <div
          className={`flex items-center justify-center size-16 rounded-full ${
            isUnavailable
              ? "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {icon}
        </div>

        <div className="space-y-2">
          <h1 className="text-xl font-medium tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>

        {domain ? (
          <code className="text-xs px-3 py-1.5 rounded-md bg-muted text-muted-foreground break-all">
            {domain}
          </code>
        ) : null}

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="gap-2"
          >
            <RefreshCw className="size-4" />
            {t("actions.retry", { defaultValue: "Try again" })}
          </Button>
          <Button asChild className="gap-2">
            <a href={`mailto:${supportEmail}`}>
              <LifeBuoy className="size-4" />
              {t("actions.contactSupport", { defaultValue: "Contact support" })}
            </a>
          </Button>
        </div>

        <a
          href={mainSite}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
        >
          {t("actions.goToMainSite", {
            brand: brand?.name,
            defaultValue: "Go to {{brand}}",
          })}
        </a>
      </div>
    </div>
  );
}
